'use client';

import React from 'react';

import useFeatureFlag from '@/hooks/useFeatureFlag';

// Bridges a backend-owned feature flag (TITAN-420) into a legacy class component, the way
// `withAppRouter` bridges the app router.
//
// Function components call `useFeatureFlag` directly. This exists for classes that haven't been
// converted yet, and it is NOT interchangeable with calling `isStoreFeatureEnabled()` inside
// `render()`: that reader is a plain function, so a class whose `mapStateToProps` doesn't select
// the settings slice would never re-render when the store-settings catalog lands, and the gated UI
// would stay hidden for the rest of the page's life. Going through the hook subscribes the wrapper
// to redux, so the class re-renders as soon as the catalog resolves.
//
// An unresolved flag reads false, which is the right default here: these gates hide extra UI, so
// the element simply appears once the catalog arrives. A gate where flag-off SKIPS A REQUIRED STEP
// wants `isStoreFeatureDisabled` instead — see the note in `@/utils/features`.
//
// `propName` must be a prop the wrapped component declares; the returned wrapper takes every other
// prop, so callers neither can nor need to pass the flag in themselves.
export function withFeatureFlag< P extends object, K extends keyof P & string >(
	Wrapped: React.ComponentType< P >,
	feature: string,
	propName: K,
) {
	const WithFeatureFlag = (props: Omit< P, K >) => {
		const enabled = useFeatureFlag(feature);
		const merged = Object.assign({}, props, { [propName]: enabled }) as P;
		return <Wrapped {...merged} />;
	};

	WithFeatureFlag.displayName = `withFeatureFlag(${feature})`;

	return WithFeatureFlag;
}

export default withFeatureFlag;
